import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Download, FileText, Award, ExternalLink, Film, GraduationCap, BookOpen } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
const mockData = [{
  week: 0,
  easi75: 0
}, {
  week: 2,
  easi75: 28
}, {
  week: 4,
  easi75: 45
}, {
  week: 8,
  easi75: 58
}, {
  week: 12,
  easi75: 65
}, {
  week: 16,
  easi75: 71
}];
export function LayerXRay() {
  const [xrayActive, setXrayActive] = useState(false);
  return <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950" data-id="element-133">
      <div className="max-w-7xl mx-auto px-6" data-id="element-134">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-12" data-id="element-135">
          <div className="flex items-center gap-3 mb-4" data-id="element-136">
            <div className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold" data-id="element-137">
              LAYER 4
            </div>
            <Eye className="w-5 h-5 text-red-600" data-id="element-138" />
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-139">
            The X-Ray Overlay
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl" data-id="element-140">
            Borrowed from the streaming world — an optional, on-demand layer of
            context that lives <em data-id="element-141">on top</em> of the scientific core, never
            inside it.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16 grid lg:grid-cols-2 gap-8 items-stretch" data-id="element-142">
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-8 flex flex-col justify-between" data-id="element-143">
            <div data-id="element-144">
              <div className="flex items-center gap-2 text-red-400 text-sm font-semibold mb-4" data-id="element-145">
                <Film className="w-4 h-4" data-id="element-146" />
                THE METAPHOR
              </div>
              <h3 className="font-editorial text-2xl font-bold text-white mb-4" data-id="element-147">
                Like Prime Video X-Ray — but for clinical evidence
              </h3>
              <p className="text-slate-300 leading-relaxed mb-4" data-id="element-148">
                On Prime Video, X-Ray lets you tap a film and instantly see who
                that actor is, what they've been in, and what song is playing.
                The story stays clean. The context appears{' '}
                <em data-id="element-149">only when asked for</em>.
              </p>
              <p className="text-slate-300 leading-relaxed" data-id="element-150">
                We bring the same idea to a Tralo speaker-deck: tap to surface
                who's speaking, which trial they're citing, and what you can
                take away — without ever crowding the slide.
              </p>
            </div>
          </div>

          <div className="relative bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-151">
            <div className="absolute top-4 left-4 z-10 bg-slate-900/90 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded" data-id="element-152">
              ← Back to menu
            </div>
            <div className="grid grid-cols-5 h-full min-h-[360px]" data-id="element-153">
              <div className="col-span-3 bg-white p-5 flex flex-col" data-id="element-154">
                <div className="text-[10px] font-semibold text-slate-500 mb-3 truncate" data-id="element-155">
                  TRALOSpeak Video Toolkit — Presenter Resource
                </div>
                <div className="grid grid-cols-2 gap-2 flex-1" data-id="element-156">
                  {[0, 1, 2, 3].map(i => <div key={i} className="bg-slate-50 border border-slate-200 rounded p-2 flex flex-col" data-id="element-157">
                      <div className="text-[8px] text-emerald-700 font-semibold mb-1 truncate" data-id="element-158">
                        Tralokinumab progressively improves response
                      </div>
                      <div className="flex-1 bg-gradient-to-tr from-emerald-50 via-emerald-100 to-emerald-300 rounded-sm" data-id="element-159" />
                    </div>)}
                </div>
                <div className="mt-3 bg-emerald-100/60 text-[9px] text-emerald-900 px-2 py-1 rounded" data-id="element-160">
                  Dr. Firas George Hougeir • ECZTRA 3 ad hoc analysis
                </div>
              </div>

              <div className="col-span-2 bg-slate-950 p-4 border-l border-red-600/40" data-id="element-161">
                <div className="text-white text-sm font-bold mb-3 tracking-wider" data-id="element-162">
                  X-RAY
                </div>
                <div className="space-y-2" data-id="element-163">
                  <div className="bg-slate-900 border border-slate-800 rounded p-2" data-id="element-164">
                    <div className="text-[10px] text-white font-semibold mb-1" data-id="element-165">
                      Dr. Firas George Hougeir
                    </div>
                    <div className="text-[8px] text-slate-400 leading-tight" data-id="element-166">
                      Dermatologic surgeon · Mayo-trained · Skin cancer
                      specialist
                    </div>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded p-2" data-id="element-167">
                    <div className="text-[9px] text-red-400 font-semibold mb-0.5" data-id="element-168">
                      THE STUDY BEHIND THIS
                    </div>
                    <div className="text-[8px] text-slate-300 leading-tight" data-id="element-169">
                      ECZTRA 3 — phase 3 trial of tralokinumab + TCS. Post hoc
                      EASI response at Week 32.
                    </div>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded p-2 text-[9px] text-slate-400" data-id="element-170">
                    Adapted from Silverberg et al., 2022
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-3 left-4 right-4 flex justify-between text-[10px] text-slate-500 uppercase tracking-wider pointer-events-none" data-id="element-171">
              <span data-id="element-172">Scientific core stays clean</span>
              <span className="text-red-400" data-id="element-173">Context on demand →</span>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-174">
          <div className="text-sm font-semibold text-slate-400 tracking-wider mb-6" data-id="element-175">
            WHAT X-RAY SURFACES
          </div>
          <div className="grid md:grid-cols-3 gap-4" data-id="element-176">
            {[{
            icon: GraduationCap,
            title: 'Speaker credentials',
            desc: 'Credentials, clinical focus, affiliations and key publications — the credibility behind the voice.'
          }, {
            icon: BookOpen,
            title: 'The study behind this',
            desc: 'The exact trial being referenced (ECZTRA 1/2/3), endpoint shown, and the citation.'
          }, {
            icon: Download,
            title: 'Downloadable assets',
            desc: 'Slide PDF, speaker bio, and citation export — so HCPs can re-present what they just learned.'
          }].map(item => <div key={item.title} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-id="element-177">
                <item.icon className="w-5 h-5 text-red-500 mb-3" data-id="element-178" />
                <div className="font-semibold text-white mb-1" data-id="element-179">
                  {item.title}
                </div>
                <div className="text-sm text-slate-400 leading-relaxed" data-id="element-180">
                  {item.desc}
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4" data-id="element-181">
          <div data-id="element-182">
            <div className="text-sm font-semibold text-red-400 tracking-wider mb-2" data-id="element-183">
              TRY IT
            </div>
            <h3 className="font-editorial text-2xl md:text-3xl font-bold text-white" data-id="element-184">
              Activate X-Ray on a live chapter
            </h3>
          </div>
          <motion.button onClick={() => setXrayActive(!xrayActive)} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors ${xrayActive ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`} data-id="element-185">
            <Eye className="w-5 h-5" data-id="element-186" />
            {xrayActive ? 'X-Ray Active' : 'Activate X-Ray'}
          </motion.button>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="relative" data-id="element-187">
          {/* Main Player (same as Layer 3) */}
          <div className="bg-white rounded-2xl border-2 border-slate-700 overflow-hidden" data-id="element-188">
            <div className="relative p-8" data-id="element-189">
              <div className="flex items-start gap-6 mb-6" data-id="element-190">
                <div className="flex-1" data-id="element-191">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-192">
                    EASI-75 Response Over 16 Weeks
                  </h3>
                  <p className="text-slate-600" data-id="element-193">ECZTRA 1 & 2 Pooled Analysis</p>
                </div>

                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-slate-200 flex-shrink-0" data-id="element-194">
                  <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop" alt="Dr. Castellanos" className="w-full h-full object-cover" data-id="element-195" />
                </div>
              </div>

              <div className="h-80" data-id="element-196">
                <ResponsiveContainer width="100%" height="100%" data-id="element-197">
                  <LineChart data={mockData} data-id="element-198">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-199" />
                    <XAxis dataKey="week" label={{
                    value: 'Weeks',
                    position: 'insideBottom',
                    offset: -5
                  }} stroke="#64748b" data-id="element-200" />
                    <YAxis label={{
                    value: 'EASI-75 Response (%)',
                    angle: -90,
                    position: 'insideLeft'
                  }} stroke="#64748b" data-id="element-201" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#1e293b',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff'
                  }} data-id="element-202" />
                    <Line type="monotone" dataKey="easi75" stroke="#E2231A" strokeWidth={3} dot={{
                    fill: '#E2231A',
                    r: 6
                  }} data-id="element-203" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* X-Ray Overlay */}
          <AnimatePresence data-id="element-204">
            {xrayActive && <motion.div initial={{
            x: '100%'
          }} animate={{
            x: 0
          }} exit={{
            x: '100%'
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200
          }} className="absolute top-0 right-0 w-full md:w-96 h-full bg-slate-950/95 backdrop-blur-sm border-l-2 border-red-600 rounded-r-2xl overflow-hidden" data-id="element-205">
                <div className="p-6 h-full overflow-y-auto" data-id="element-206">
                  {/* Speaker Info */}
                  <div className="mb-8" data-id="element-207">
                    <div className="flex items-center gap-2 text-red-400 text-sm font-semibold mb-4" data-id="element-208">
                      <Award className="w-4 h-4" data-id="element-209" />
                      SPEAKER PROFILE
                    </div>

                    <div className="flex items-center gap-4 mb-4" data-id="element-210">
                      <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop" alt="Dr. Castellanos" className="w-16 h-16 rounded-full object-cover border-2 border-red-600" data-id="element-211" />
                      <div data-id="element-212">
                        <div className="font-bold text-white" data-id="element-213">
                          Dr. María Castellanos
                        </div>
                        <div className="text-sm text-slate-400" data-id="element-214">
                          Buenos Aires, Argentina
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm" data-id="element-215">
                      <div className="flex items-center justify-between py-2 border-b border-slate-800" data-id="element-216">
                        <span className="text-slate-400" data-id="element-217">H-index</span>
                        <span className="text-white font-semibold" data-id="element-218">42</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-slate-800" data-id="element-219">
                        <span className="text-slate-400" data-id="element-220">Publications</span>
                        <span className="text-white font-semibold" data-id="element-221">120+</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-slate-800" data-id="element-222">
                        <span className="text-slate-400" data-id="element-223">Affiliations</span>
                        <span className="text-white font-semibold" data-id="element-224">
                          EADV Board
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Study Reference */}
                  <div className="mb-8" data-id="element-225">
                    <div className="flex items-center gap-2 text-red-400 text-sm font-semibold mb-4" data-id="element-226">
                      <FileText className="w-4 h-4" data-id="element-227" />
                      NOW REFERENCING
                    </div>

                    <div className="bg-slate-900 rounded-lg p-4 border border-slate-800" data-id="element-228">
                      <div className="font-semibold text-white mb-2" data-id="element-229">
                        ECZTRA 3 Trial
                      </div>
                      <div className="text-sm text-slate-400 mb-3" data-id="element-230">
                        Phase 3, randomized, double-blind, placebo-controlled
                        study of tralokinumab monotherapy in adults with
                        moderate-to-severe atopic dermatitis
                      </div>
                      <button className="text-red-400 text-sm flex items-center gap-1 hover:text-red-300 transition-colors" data-id="element-231">
                        View full study <ExternalLink className="w-3 h-3" data-id="element-232" />
                      </button>
                    </div>
                  </div>

                  {/* Download Options */}
                  <div data-id="element-233">
                    <div className="flex items-center gap-2 text-red-400 text-sm font-semibold mb-4" data-id="element-234">
                      <Download className="w-4 h-4" data-id="element-235" />
                      DOWNLOAD RESOURCES
                    </div>

                    <div className="space-y-3" data-id="element-236">
                      <button className="w-full bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-red-600 rounded-lg p-4 text-left transition-colors group" data-id="element-237">
                        <div className="flex items-center justify-between" data-id="element-238">
                          <div data-id="element-239">
                            <div className="font-medium text-white mb-1" data-id="element-240">
                              Slide Deck (PDF)
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-241">
                              Complete presentation slides
                            </div>
                          </div>
                          <Download className="w-5 h-5 text-slate-400 group-hover:text-red-400 transition-colors" data-id="element-242" />
                        </div>
                      </button>

                      <button className="w-full bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-red-600 rounded-lg p-4 text-left transition-colors group" data-id="element-243">
                        <div className="flex items-center justify-between" data-id="element-244">
                          <div data-id="element-245">
                            <div className="font-medium text-white mb-1" data-id="element-246">
                              Study Citation
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-247">
                              BibTeX & APA formats
                            </div>
                          </div>
                          <Download className="w-5 h-5 text-slate-400 group-hover:text-red-400 transition-colors" data-id="element-248" />
                        </div>
                      </button>

                      <button className="w-full bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-red-600 rounded-lg p-4 text-left transition-colors group" data-id="element-249">
                        <div className="flex items-center justify-between" data-id="element-250">
                          <div data-id="element-251">
                            <div className="font-medium text-white mb-1" data-id="element-252">
                              Speaker Bio
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-253">
                              Full CV and credentials
                            </div>
                          </div>
                          <Download className="w-5 h-5 text-slate-400 group-hover:text-red-400 transition-colors" data-id="element-254" />
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="text-center text-slate-400 mt-12 italic" data-id="element-255">
          The differentiator — rich context without disrupting the scientific
          core
        </motion.p>
      </div>
    </section>;
}